import { inject } from '@angular/core';
import { Router, UrlTree, ActivatedRouteSnapshot } from '@angular/router';
import { CanActivateFn } from '@angular/router';
import { PermissionsService } from '../services/permissions.service';
import { AuthService } from '../services/auth.service';

export const PermissionsGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot
): Promise<boolean | UrlTree> => {
  const permissionsService = inject(PermissionsService);
  const router = inject(Router);
  const authService = inject(AuthService);

  // First check if user is logged in
  if (!authService.isLoggedIn()) {
    return router.createUrlTree(['/login']);
  }

  // Build the route path - use route.routeConfig?.path or construct from URL
  let routePath = '';
  
  // Get the current route path segment
  const currentPath = route.url.map((segment) => segment.path).join('/');
  
  // Build full path - check if we're in ai-agent routes
  if (route.parent?.routeConfig?.path === 'ai-agent') {
    routePath = `/ai-agent/${currentPath}`;
  } else {
    // Fallback: construct from URL segments
    routePath = '/' + currentPath;
  }
  
  // Handle teams/:id route - normalize to /ai-agent/teams for permission check
  if (routePath.includes('/teams/') && routePath !== '/ai-agent/teams') {
    routePath = '/ai-agent/teams';
  }

  // Load permissions if not already loaded
  await permissionsService.loadMenuPermissions();

  // Check if user has permission for this route
  const hasPermission = permissionsService.hasRoutePermission(routePath);

  if (!hasPermission) {
    // Redirect to chat or first available route with permission
    console.warn(`Access denied to route: ${routePath}`);
    
    // Try to find first available route with permission
    const availableRoutes = ['/ai-agent/chat', '/ai-agent/agents', '/ai-agent/prompts', '/ai-agent/models', '/ai-agent/kb', '/ai-agent/teams'];
    const firstAllowedRoute = availableRoutes.find(r => permissionsService.hasRoutePermission(r));
    
    return router.createUrlTree([firstAllowedRoute || '/not-found']);
  }

  return true;
};

